import ph, { type PostHog } from 'posthog-js'
import { type App, nextTick } from 'vue'
import type { Router } from 'vue-router'

let posthog: void | PostHog

/** Same gate as app-next: key present and not `yarn serve` (import.meta.env.DEV). */
const shouldInitializePosthog = Boolean(import.meta.env.CAPP_POSTHOG_PROJECT_API_KEY) && !import.meta.env.DEV

const APP_NAME = 'signal-collection'

export type CollectionAnalyticsContext = {
  organization_id?: number | null
  project_id?: string | null
  signal_id?: string | null
  session_id?: string | null
  is_preview?: boolean | null
  link_token?: string | null
}

function assertPosthogInitialized(instance: void | PostHog): asserts instance is PostHog {
  if (!instance) {
    throw new Error('PostHog is not initialized but it should have been. Check your environment configuration.')
  }
}

function compactContext(context: CollectionAnalyticsContext): Record<string, string | number | boolean> {
  const entries = Object.entries({
    app: APP_NAME,
    organization_id: context.organization_id,
    project_id: context.project_id,
    signal_id: context.signal_id,
    session_id: context.session_id,
    is_preview: context.is_preview,
    link_token: context.link_token,
  }).filter(([, value]) => value !== undefined && value !== null)

  return Object.fromEntries(entries) as Record<string, string | number | boolean>
}

export const usePosthog = (app: App, router: Router) => {
  if (!shouldInitializePosthog) return

  posthog = ph.init(import.meta.env.CAPP_POSTHOG_PROJECT_API_KEY, {
    api_host: import.meta.env.CAPP_POSTHOG_INSTANCE_ADDRESS,
    // Respondents are anonymous — never call identify(); no person profiles.
    person_profiles: 'identified_only',
    capture_pageview: false,
    disable_session_recording: import.meta.env.MODE === 'development' || import.meta.env.MODE === 'localapi',
    enable_heatmaps: true,
    tracing_headers: [
      import.meta.env.CAPP_REMOTE_API_URL ? import.meta.env.CAPP_REMOTE_API_URL.replace('https://', '') : '',
    ],
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
  })

  app.config.globalProperties.$posthog = posthog

  router.afterEach((to, _from, failure) => {
    if (failure) return

    nextTick(() => {
      if (!app.config.globalProperties.$posthog) return

      app.config.globalProperties.$posthog.capture('$pageview', {
        path: to.fullPath,
        app: APP_NAME,
      })
    })
  })
}

/** Attach signal context as super properties so anonymous events stay filterable. */
export function registerCollectionContext(context: CollectionAnalyticsContext) {
  if (!shouldInitializePosthog) return

  assertPosthogInitialized(posthog)

  posthog.register({
    app_version: import.meta.env.CAPP_VERSION,
    ...compactContext(context),
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function posthogException(event: string, properties: Record<string, any> = {}) {
  if (!shouldInitializePosthog) return

  assertPosthogInitialized(posthog)

  posthog.captureException(event, {
    app: APP_NAME,
    ...properties,
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function posthogCapture(event: string, properties: Record<string, any> = {}) {
  if (!shouldInitializePosthog) return

  assertPosthogInitialized(posthog)

  posthog.capture(event, {
    app: APP_NAME,
    ...properties,
  })
}
